import React from "react";
import * as Mui from "@mui/material";
import { makeStyles } from "tss-react/mui";

const useStyles = makeStyles()((theme) => ({
	label: {
		display: "block",
		marginBottom: 8,
		fontSize: 14,
		lineHeight: "20px",
		color: theme.palette.custom.textSecondary,
	},
	asterisk: {
		color: theme.palette.error.main,
		marginLeft: 2,
	},

	control: {
		"& .MuiOutlinedInput-root": { borderRadius: 4 },
		"& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.custom.borderCard },
		"& .MuiOutlinedInput-input": { padding: "12px 16px" },
		"& .MuiSelect-select": { padding: "12px 16px" },
	},
	error: {
		color: theme.palette.error.main,
		fontSize: "0.75rem",
		fontWeight: "bold",
		marginTop: 4,
	},
}));

interface LabeledControlProps {
	label?: string;
	required?: boolean;
	error?: boolean;
	helperText?: React.ReactNode;
}

/** Label-above wrapper for any control (keeps consistent label spacing across the form). */
export const LabeledControl: React.FC<LabeledControlProps> = ({ label, required, error, helperText, children }) => {
	const { classes } = useStyles();
	return (
		<div>
			{label !== undefined && (
				<Mui.Typography component="label" className={classes.label}>
					{label}
					{required && <span className={classes.asterisk}>*</span>}
				</Mui.Typography>
			)}
			{children}
			{error && helperText && <Mui.Typography className={classes.error}>{helperText}</Mui.Typography>}
		</div>
	);
};

interface Props {
	label?: string;
	value: string;
	onChange: (value: string) => void;
	required?: boolean;
	error?: boolean;
	helperText?: React.ReactNode;
	type?: string;
	multiline?: boolean;
	rows?: number;
	endAdornment?: React.ReactNode;
	select?: boolean;
	placeholder?: string;
	autoComplete?: string;
	readOnly?: boolean;
	onInteract?: () => void;
	children?: React.ReactNode;
}

/** Label-above text/select field matching the Q2 refresh design (no floating MUI label). */
export const LabeledField: React.FC<Props> = ({
	label, value, onChange, required, error, helperText, type = "text", multiline, rows, endAdornment, select, placeholder, autoComplete, readOnly, onInteract, children,
}) => {
	const { classes } = useStyles();
	return (
		<LabeledControl label={label} required={required} error={error} helperText={helperText}>
			<Mui.TextField
				fullWidth
				variant="outlined"
				className={classes.control}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				type={type}
				error={error}
				multiline={multiline}
				rows={rows}
				select={select}
				placeholder={placeholder}
				autoComplete={autoComplete}
				onMouseDown={onInteract}
				onFocus={onInteract}
				InputProps={{
					readOnly,
					endAdornment: endAdornment ? <Mui.InputAdornment position="end">{endAdornment}</Mui.InputAdornment> : undefined,
				}}
			>
				{children}
			</Mui.TextField>
		</LabeledControl>
	);
};

export const useLabeledControlStyles = useStyles;
